/**
 * Themed Word Search — game surface.
 *
 * Standard rectangular grid (size from difficulty profile). Tap-two
 * selection: tap a start cell, tap an end cell, the engine evaluates the
 * straight line in any of 8 directions against the placed words.
 *
 * What's themed (NOT the grid mechanic itself):
 *  - per-category warm palette
 *  - per-category painterly atmosphere image behind the grid (8–15% opacity)
 *  - per-category micro-motif glyph that travels from grid to found-list
 *  - per-category chime on found events + ambient sound palette
 *
 * No silhouette ornaments. No silhouette watermark. No silhouette glyph
 * beside the title. The shape concept was removed; theme lives in palette
 * + atmosphere image + motif + chime.
 *
 * Flow (`useWordSearchEngine`): intro → play → done.
 */
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';

import { triggerHaptic } from '@/juice';
import { AtmosphereSurface, buildSrcSet, variantOf } from '@/juice/RevealedImage';
import { cellKey, useWordSearchEngine, type Cell } from '@/games/word-search/engine';
import type {
  GameController,
  GameModule,
  GameModuleDefinition,
  GameSession,
  MountOptions,
} from '@rendever/games-sdk';
import { HostProvider } from '@/host/HostContext';
import { PreferencesProvider, usePreferences } from '@/preferences';

import { Affirmation } from './Affirmation';
import { Celebration } from './Celebration';
import { LETTER_STAGGER_MS, WORD_LIST_STAGGER_MS, letterSpring } from './motion';
import { paletteFor, paletteToCssVars } from './palette';
import { sound } from './sound';
import {
  atmosphereSrc,
  dailyWordSearchSeed,
  getThemeSurface,
  sampleWordsForRound,
  THEME_SURFACES,
  WORDS_PER_ROUND,
  type CategorySlug,
  type ThemeSurface,
} from './themes';

import './Game.css';

const definition: GameModuleDefinition = {
  slug: 'word-search',
  title: 'Word Search',
  family: 'word',
  runtime: 'dom',
  cognitiveDomains: ['language', 'visuospatial', 'attention'],
};

// ---------------------------------------------------------------------------
// SDK adapter
// ---------------------------------------------------------------------------

export const ThemedShapeWordSearchGame: GameModule = {
  definition,
  async mount(opts: MountOptions): Promise<GameController> {
    const { container, session, difficulty, signal } = opts;
    container.replaceChildren();
    const host = document.createElement('div');
    host.className = 'ts-game-host';
    container.append(host);
    const root: Root = createRoot(host);
    root.render(
      <HostProvider>
        <PreferencesProvider>
          <GameView session={session} difficulty={difficulty} />
        </PreferencesProvider>
      </HostProvider>,
    );
    const abortHandler = (): void => {
      root.unmount();
      host.remove();
    };
    signal.addEventListener('abort', abortHandler, { once: true });
    return {
      pause(): void {
        void session.pause().catch(() => {});
      },
      resume(): void {
        void session.resume().catch(() => {});
      },
      dispose(): void {
        signal.removeEventListener('abort', abortHandler);
        root.unmount();
        host.remove();
      },
    };
  },
};

export default ThemedShapeWordSearchGame;

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

interface ViewProps {
  session: GameSession;
  difficulty: MountOptions['difficulty'];
}

const GRID_SIZE_DEFAULT = 12;
const GRID_SIZE_PHONE = 10;
const PHONE_BREAKPOINT_PX = 430;

/**
 * Pick a grid size based on the current viewport width. Phones (≤430px)
 * get a 10×10 grid so cells stay finger-friendly and the words list can
 * sit beside the grid as a compact chip strip; everything wider keeps the
 * canonical 12×12.
 */
function useResponsiveGridSize(): number {
  const [size, setSize] = useState<number>(() => {
    if (typeof window === 'undefined') return GRID_SIZE_DEFAULT;
    return window.innerWidth <= PHONE_BREAKPOINT_PX ? GRID_SIZE_PHONE : GRID_SIZE_DEFAULT;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(`(max-width: ${PHONE_BREAKPOINT_PX}px)`);
    const onChange = (): void => {
      setSize(mql.matches ? GRID_SIZE_PHONE : GRID_SIZE_DEFAULT);
    };
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return size;
}

function GameView({ session, difficulty }: ViewProps): React.JSX.Element {
  const { theme: themeMode } = usePreferences();
  const gridSize = useResponsiveGridSize();

  // ?category=... → which themed category. Default 'garden'.
  const requestedSlug = useMemo<CategorySlug | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get('category') ?? params.get('theme'); // accept legacy `theme`
      if (!raw) return null;
      const match = THEME_SURFACES.find((t) => t.slug === raw);
      return match?.slug ?? null;
    } catch {
      return null;
    }
  }, []);

  const initialMode = useMemo<'daily' | 'practice'>(() => {
    if (typeof window === 'undefined') return 'daily';
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('mode') === 'practice' ? 'practice' : 'daily';
    } catch {
      return 'daily';
    }
  }, []);

  const initialCategorySlug = requestedSlug ?? 'garden';
  const [playMode, setPlayMode] = useState<'daily' | 'practice'>(initialMode);
  const [categorySlug, setCategorySlug] = useState<CategorySlug>(initialCategorySlug);
  const [seed, setSeed] = useState<number>(() =>
    initialMode === 'daily'
      ? dailyWordSearchSeed(initialCategorySlug)
      : Math.floor(Math.random() * 2 ** 31),
  );

  const surface = getThemeSurface(categorySlug);
  // Sample WORDS_PER_ROUND from the category's full pool. The pool is
  // ~50-60 words wide; the grid only fits ~8. The same (category, seed)
  // pair always picks the same sample, so today's daily puzzle is stable
  // across the community and re-rolls give a genuinely fresh set rather
  // than reshuffling the same 8.
  const words = useMemo(
    () => sampleWordsForRound(surface.words, WORDS_PER_ROUND, seed),
    [surface.words, seed],
  );

  const [completeDurationMs, setCompleteDurationMs] = useState<number>(0);

  const { state, actions } = useWordSearchEngine({
    session,
    difficulty,
    words,
    gridSize,
    seed,
    themeSlug: categorySlug,
    onAllFound: (durationMs) => {
      setCompleteDurationMs(durationMs);
      sound.play('complete');
      triggerHaptic('success');
    },
  });

  // Skip the intermediate "Today's puzzle / Begin" screen entirely — when
  // the player taps Start on a hub card, drop them straight into the
  // playable grid. The word list lives in the right-hand panel during
  // gameplay, so no separate "see the words first" screen is needed.
  //
  // We watch `state.phase`: any time the engine resets to 'intro' (initial
  // mount, switch-category, re-roll), kick the round off immediately.
  useEffect(() => {
    if (state.phase !== 'intro') return;
    sound.unlock();
    actions.start();
    // We intentionally depend only on phase so we don't re-fire on every
    // `actions` re-creation. The engine's `start` is idempotent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  // After completion: re-roll THIS category with a fresh seed. The seed
  // change re-derives both the sampled word set and the grid placement,
  // so the same category yields a different round each time.
  const handleReroll = useCallback(() => {
    sound.play('cardEnter');
    triggerHaptic('light');
    setPlayMode('practice');
    const newSeed = Math.floor(Math.random() * 2 ** 31);
    setSeed(newSeed);
    const nextWords = sampleWordsForRound(surface.words, WORDS_PER_ROUND, newSeed);
    actions.anotherRound(nextWords, newSeed);
  }, [actions, surface.words]);

  // After completion: rotate to the next category. Sample a fresh word
  // set for the next category using the new seed so the engine doesn't
  // get the full ~57-word pool dumped on it.
  const handleNextCategory = useCallback(() => {
    sound.play('cardEnter');
    triggerHaptic('light');
    const i = THEME_SURFACES.findIndex((c) => c.slug === categorySlug);
    const nextSurface = THEME_SURFACES[(i + 1) % THEME_SURFACES.length]!;
    setCategorySlug(nextSurface.slug);
    const newSeed =
      playMode === 'daily'
        ? dailyWordSearchSeed(nextSurface.slug)
        : Math.floor(Math.random() * 2 ** 31);
    setSeed(newSeed);
    const nextWords = sampleWordsForRound(nextSurface.words, WORDS_PER_ROUND, newSeed);
    actions.anotherRound(nextWords, newSeed);
  }, [actions, categorySlug, playMode]);

  const handleBack = useCallback(() => {
    sound.play('cardEnter');
    triggerHaptic('light');
    void actions.end();
  }, [actions]);

  // Inject the per-category palette as CSS variables on the surface.
  const cssVars = useMemo(
    () => paletteToCssVars(paletteFor(categorySlug, themeMode)),
    [categorySlug, themeMode],
  );

  return (
    <div
      className="ts-game"
      data-theme={themeMode}
      data-category={categorySlug}
      style={cssVars as React.CSSProperties}
    >
      <Atmosphere surface={surface} themeMode={themeMode} />

      <AnimatePresence mode="wait">
        {state.phase === 'play' || state.phase === 'intro' ? (
          <PlaySurface
            key={`play-${categorySlug}-${state.roundKey}`}
            surface={surface}
            mode={playMode}
            state={state}
            actions={actions}
          />
        ) : (
          <Affirmation
            key={`done-${categorySlug}-${state.roundKey}`}
            surface={surface}
            durationMs={completeDurationMs}
            wordsFound={state.found.length}
            wordsTotal={state.totalWords}
            mode={playMode}
            onAnother={handleReroll}
            onNext={handleNextCategory}
            onBack={handleBack}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Atmosphere — the painterly per-category field behind the grid.
//
// Tries the generated image first; if it 404s, the CSS-only fallback below
// provides a soft per-palette bloom. Either way, the atmosphere sits at
// 8–15% opacity and never competes with the grid for attention.
// ---------------------------------------------------------------------------

function Atmosphere({
  surface,
  themeMode,
}: {
  surface: ThemeSurface;
  themeMode: 'light' | 'dark';
}): React.JSX.Element {
  const lightSrc = atmosphereSrc(surface.slug, 'light');
  const darkSrc = atmosphereSrc(surface.slug, 'dark');
  const src = themeMode === 'dark' ? darkSrc : lightSrc;
  const [imgOk, setImgOk] = useState(true);

  // Probe the image once on slug change; if it 404s, fall back to CSS-only.
  useEffect(() => {
    setImgOk(true);
    const img = new Image();
    img.onload = () => setImgOk(true);
    img.onerror = () => setImgOk(false);
    img.src = src;
  }, [src]);

  return (
    <>
      <div className="ts-game__paper" aria-hidden="true" />
      {imgOk ? (
        <div className="ts-game__atm-image" aria-hidden="true">
          <AtmosphereSurface
            src={src}
            avifSrc={variantOf(src, 'avif')}
            webpSrc={variantOf(src, 'webp')}
            // Wordsearch-themedshape canonicals are square 1024×1024, so the
            // asset pipeline correctly elides the 1366w variant (upscale).
            // Advertising it anyway makes the browser fetch a non-existent
            // file; Vite's SPA fallback returns 200/index.html and the image
            // decodes as 0×0. Stick to widths the pipeline actually emits.
            avifSrcSet={buildSrcSet(src, 'avif', [430, 860])}
            webpSrcSet={buildSrcSet(src, 'webp', [430, 860])}
            sizes="100vw"
            eager
          />
        </div>
      ) : (
        <div className="ts-game__atm-bloom" aria-hidden="true" />
      )}
      <div className="ts-game__paper-noise" aria-hidden="true" />
    </>
  );
}

// ---------------------------------------------------------------------------
// PlaySurface — header + rectangular grid + words sidebar + celebration overlay.
// ---------------------------------------------------------------------------

interface PlaySurfaceProps {
  surface: ThemeSurface;
  mode: 'daily' | 'practice';
  state: ReturnType<typeof useWordSearchEngine>['state'];
  actions: ReturnType<typeof useWordSearchEngine>['actions'];
}

function PlaySurface({ surface, mode, state, actions }: PlaySurfaceProps): React.JSX.Element {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const foundListRef = useRef<HTMLUListElement | null>(null);

  const foundLockedSet = useMemo(() => {
    const s = new Set<string>();
    for (const r of state.found) for (const c of r.cells) s.add(cellKey(c));
    return s;
  }, [state.found]);

  const handleTap = useCallback(
    (rc: Cell) => {
      const wasEmpty = state.start === null;
      if (wasEmpty) {
        sound.play('letterSelect');
        triggerHaptic('light');
      } else {
        sound.play('letterTap');
      }
      actions.tapCell(rc);
    },
    [actions, state.start],
  );

  // Themed chime + travel whoosh on every new found word.
  const lastCelebRef = useRef<string | null>(null);
  useEffect(() => {
    const c = state.lastCelebration;
    if (!c) return;
    if (lastCelebRef.current === c.word) return;
    lastCelebRef.current = c.word;
    sound.playChime(surface.chime);
    window.setTimeout(() => sound.play('wordTravel'), 120);
    triggerHaptic('success');
  }, [state.lastCelebration, surface.chime]);

  const missSizeRef = useRef(0);
  useEffect(() => {
    if (state.missCells.size > 0 && missSizeRef.current === 0) {
      sound.play('incorrectSoft');
    }
    missSizeRef.current = state.missCells.size;
  }, [state.missCells]);

  // Derive grid size from the letters array. The engine ships a square grid.
  const gridSize = state.letters.length;

  // Inline isOnAxis to drive the dim-unreachable affordance.
  const reachableKeys = useMemo<Set<string>>(() => {
    if (!state.start) return new Set();
    const s = new Set<string>();
    for (let r = 0; r < gridSize; r += 1) {
      for (let c = 0; c < gridSize; c += 1) {
        const rc: Cell = [r, c];
        if (rc[0] === state.start[0] && rc[1] === state.start[1]) continue;
        const dr = rc[0] - state.start[0];
        const dc = rc[1] - state.start[1];
        if (dr === 0 || dc === 0 || Math.abs(dr) === Math.abs(dc)) {
          s.add(cellKey(rc));
        }
      }
    }
    return s;
  }, [state.start, gridSize]);

  return (
    <section className="ts-play">
      <header className="ts-play__header">
        <div className="ts-play__heading-text">
          <p className="ts-play__eyebrow">
            {mode === 'daily' ? 'Daily Word Search' : 'Practice Word Search'} &middot;{' '}
            {surface.title}
          </p>
          <h2 className="ts-play__title">Find these {state.totalWords} words.</h2>
          <p className="ts-play__hint">
            Tap a letter to start, then tap another to finish the word.
          </p>
        </div>
        <p
          className="ts-play__progress"
          aria-live="polite"
          aria-label={`Found ${state.found.length} of ${state.totalWords}`}
        >
          <span className="ts-play__progress-num">{state.found.length}</span>
          <span className="ts-play__progress-sep">/</span>
          <span className="ts-play__progress-total">{state.totalWords}</span>
          <span className="ts-play__progress-label">found</span>
        </p>
      </header>

      <div className="ts-play__stage">
        <div
          ref={gridRef}
          className="ts-play__grid"
          role="grid"
          aria-label={`Word search grid, ${gridSize} by ${gridSize}`}
          style={{ ['--ws-cols' as string]: String(gridSize) } as React.CSSProperties}
        >
          {state.letters.map((row, r) =>
            row.map((ch, c) => {
              const rc: Cell = [r, c];
              const key = cellKey(rc);
              const isStart = state.start ? state.start[0] === r && state.start[1] === c : false;
              const isFoundLocked = foundLockedSet.has(key);
              const isCelebrating = state.celebratingCells.has(key);
              const isMiss = state.missCells.has(key);
              const isReachable = reachableKeys.has(key);
              // Diagonal cascade — cells on the same (r+c) diagonal enter
              // together, so the total reveal is bounded by 2*(N-1) steps
              // rather than N*N. ~480ms total at 22ms/step on a 12x12 grid.
              const staggerS = (r + c) * LETTER_STAGGER_MS;
              return (
                <motion.button
                  type="button"
                  data-mobile-audit-ignore="dense-board"
                  key={key}
                  className="ts-cell"
                  data-found={isFoundLocked ? 'true' : undefined}
                  data-celebrating={isCelebrating ? 'true' : undefined}
                  data-start={isStart ? 'true' : undefined}
                  data-reachable={isReachable ? 'true' : undefined}
                  data-miss={isMiss ? 'true' : undefined}
                  aria-pressed={isStart || isFoundLocked}
                  aria-label={
                    isStart
                      ? `${ch}, start of selection`
                      : isFoundLocked
                        ? `${ch}, part of a found word`
                        : ch
                  }
                  initial={{ opacity: 0, y: 6 }}
                  animate={{
                    opacity: 1,
                    y: isCelebrating ? -6 : 0,
                    scale: isCelebrating ? 1.15 : 1,
                  }}
                  transition={{ ...letterSpring, delay: staggerS / 1000 }}
                  onClick={() => handleTap(rc)}
                >
                  <span className="ts-cell__letter">{ch}</span>
                </motion.button>
              );
            }),
          )}
        </div>

        <aside className="ts-play__words" aria-label="Words to find">
          <p className="ts-play__words-eyebrow">Words</p>
          <ul className="ts-play__words-list" ref={foundListRef}>
            {state.placements.map((p, i) => {
              const isFound = state.found.find((r) => r.word === p.word);
              const jitter = strikeJitter(p.word);
              return (
                <motion.li
                  key={p.word}
                  className="ts-play__words-item"
                  data-found={isFound ? 'true' : undefined}
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    ...letterSpring,
                    delay: (i * WORD_LIST_STAGGER_MS) / 1000,
                  }}
                >
                  {/* Per-word strikethrough — angle, position, length, and
                   * weight all vary deterministically from the word so the
                   * marks read as hand-drawn instead of CSS-stamped. The
                   * `--strike-width` only takes effect once data-found
                   * flips on. */}
                  <span
                    className="ts-play__words-strike"
                    aria-hidden="true"
                    style={
                      {
                        ['--strike-rotate' as string]: `${jitter.rotateDeg}deg`,
                        ['--strike-top' as string]: `calc(50% + ${jitter.topPx}px)`,
                        ['--strike-left' as string]: `${jitter.leftPct}%`,
                        ['--strike-width' as string]: `${jitter.widthPct}%`,
                        ['--strike-height' as string]: `${jitter.heightPx}px`,
                      } as React.CSSProperties
                    }
                  />
                  <span className="ts-play__words-text">{p.word}</span>
                </motion.li>
              );
            })}
          </ul>
        </aside>
      </div>

      <Celebration
        surface={surface}
        event={state.lastCelebration}
        gridRef={gridRef}
        foundListRef={foundListRef}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Strike jitter — deterministic per-word variation for the strikethrough
// mark on the "words to find" list. Keeps the strikes from reading as
// CSS-stamped uniform lines; they look hand-drawn instead.
//
// Stable: same word → same jitter, every render. Seeded from the word
// itself via a tiny string hash.
// ---------------------------------------------------------------------------

interface StrikeJitter {
  /** Rotation in degrees, ±4° around horizontal. */
  rotateDeg: number;
  /** Vertical offset from the word's center, in px. ±3px. */
  topPx: number;
  /** Left-edge offset, in %. -5% to +3% — some marks overshoot left. */
  leftPct: number;
  /** Width as a % of the item box. 85–115% — some end short, some past. */
  widthPct: number;
  /** Line weight in px. 1.5–2.5. */
  heightPx: number;
}

function strikeJitter(word: string): StrikeJitter {
  // FNV-1a-ish 32-bit hash. Deterministic per word.
  let h = 2166136261 >>> 0;
  for (let i = 0; i < word.length; i += 1) {
    h ^= word.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  // Pull five independent jitter sources from the hash.
  const r1 = (h & 0xff) / 255; // angle
  const r2 = ((h >>> 8) & 0xff) / 255; // top
  const r3 = ((h >>> 16) & 0xff) / 255; // left
  const r4 = ((h >>> 24) & 0xff) / 255; // width
  const r5 = (((h * 2654435761) >>> 0) & 0xff) / 255; // weight

  return {
    rotateDeg: -4 + r1 * 8, // ±4°
    topPx: -3 + r2 * 6, // ±3px
    leftPct: -5 + r3 * 8, // -5..+3
    widthPct: 85 + r4 * 30, // 85..115
    heightPx: 1.5 + r5 * 1.0, // 1.5..2.5
  };
}
